<script setup>
import { ref, computed, defineEmits, onMounted } from 'vue';
import { useEventListener } from '@vueuse/core';

const { closeOnBackdropClick, onClose } = defineProps({
  closeOnBackdropClick: { type: Boolean, default: true },
  onClose: { type: Function, required: true },
});

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });

const paneClassName = computed(() => {
  return `pane-mask skip-context-menu`;
});

const mousedDownOnBackdrop = ref(false);

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const close = () => {
  show.value = false;
  emit('close');
  onClose();
};

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (closeOnBackdropClick) {
      close();
    }
  }
};

const onKeydown = e => {
  if (show.value && e.code === 'Escape') {
    close();
    e.stopPropagation();
  }
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);

onMounted(() => {
  if (import.meta.env.DEV && onClose && typeof onClose === 'function') {
    // eslint-disable-next-line no-console
    console.warn(
      "[DEPRECATED] The 'onClose' prop is deprecated. Please use the 'close' event instead."
    );
  }
});
</script>

<template>
  <transition name="pane">
    <div
      v-if="show"
      class="fixed top-0 right-0 h-full w-full bg-neutral-950/75 backdrop-blur-sm z-[9990] pane-mask skip-context-menu"
      @mousedown="handleMouseDown"
    >
      <div
        class="h-full w-[64rem] max-w-full max-h-full overflow-auto bg-white shadow-lg absolute right-0 skip-context-menu pane-container"
        @mouse.stop
        @mousedown="event => event.stopPropagation()"
      >
        <slot />
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.pane-enter-active,
.pane-leave-active {
  transition: opacity 0.3s ease;

  .pane-container {
    transition: transform 0.3s ease;
  }
}

.pane-enter-from,
.pane-leave-to {
  opacity: 0;

  .pane-container {
    transform: translateX(100%);
  }
}
</style>
